/**
 * Core hook for plugin builder session management.
 * Handles session CRUD, streaming, guided flow state, undo/redo, verbose events, and screenshots.
 */

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useStreaming } from '@/lib/hooks/useStreaming';
import { authApi } from '@/lib/auth-api';
import api from '@/lib/api';
import { addBreadcrumb } from '@/lib/telemetry';
import { useUndoRedo } from './useUndoRedo';
import { loadHistory, saveHistory, cleanupOldEntries } from '../lib/history-store';
import type {
  PluginBuilderSession,
  VirtualFileSystem,
  GuidedPhase,
  GuidedFlow,
  BuildPlan,
  BuilderMode,
  VerboseEvent,
} from '../types';

const WEBSOCKET_ENDPOINT = import.meta.env.VITE_WEBSOCKET_ENDPOINT || '';

interface UsePluginBuilderSessionOptions {
  verbose?: boolean;
  useOpus?: boolean;
  onVerboseEvent?: (event: VerboseEvent) => void;
  onError?: (err: Error) => void;
}

export interface UsePluginBuilderSessionReturn {
  // Session state
  session: PluginBuilderSession | null;
  isLoading: boolean;
  error: string | null;

  // Files
  files: VirtualFileSystem | null;

  // Conversation
  messages: Array<{ role: 'user' | 'assistant'; content: string; timestamp?: string }>;
  streamingText: string;
  isSending: boolean;

  // Guided flow
  guidedFlow: GuidedFlow | null;
  buildPlan: BuildPlan | null;
  builderMode: BuilderMode;
  isGuidedLoading: boolean;
  guidedError: string | null;

  // Undo/redo
  canUndo: boolean;
  canRedo: boolean;
  undoCount: number;
  redoCount: number;
  undo: () => void;
  redo: () => void;

  // Actions
  loadSession: (sessionId: string) => Promise<void>;
  sendMessage: (message: string, guidedPhase?: GuidedPhase, autoApprove?: boolean) => Promise<void>;
  startGuidedFlow: (idea: string) => Promise<void>;
  setBuilderMode: (mode: BuilderMode) => void;
  saveSession: (name: string, description?: string) => Promise<void>;
  resetContext: () => Promise<void>;
  stopGeneration: () => void;
  uploadPreviewScreenshot: (dataUrl: string) => void;
  advanceStep: () => Promise<void>;

  // Programmatic guided flow actions
  submitAnswers: (answers: Record<number, string>, comment?: string) => Promise<void>;
  approvePlan: () => Promise<void>;
  requestPlanChanges: (feedback: string) => Promise<void>;
  approveAssumptions: () => Promise<void>;
  suggestAssumptionChanges: (comments: Record<string, string>) => Promise<void>;
  applyPendingGuidedResult: () => void;
  hasPendingGuidedResult: boolean;
  cancelGuidedRequest: () => void;
  rewindToPhase: (targetPhase: GuidedPhase) => Promise<void>;

  // Preview
  buildPreview: (files?: VirtualFileSystem) => Promise<string | null>;
  previewHtml: string | null;
  isBuildingPreview: boolean;
  buildError: string | null;

  // State indicators
  isResetting: boolean;

  // SDK update
  sdkUpdate: { available: boolean; from: string | null; to: string; hasBreaking: boolean } | null;
  isUpgradingSdk: boolean;
  upgradeSdk: () => Promise<void>;

  /** Optimistically set the session name (header updates immediately) */
  setSessionName: (name: string) => void;

  /** Optimistically update session settings (so config panel reflects changes without reload) */
  updateSessionSettings: (settings: Record<string, unknown>) => void;

  /** Optimistically update the build plan state (e.g. toggling node showUI) */
  setBuildPlan: (plan: BuildPlan | null) => void;
}

export function usePluginBuilderSession(
  options: UsePluginBuilderSessionOptions = {},
): UsePluginBuilderSessionReturn {
  const { verbose = false, useOpus = false, onVerboseEvent, onError } = options;

  const authToken = authApi.getIdToken();
  const [session, setSession] = useState<PluginBuilderSession | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [files, setFiles] = useState<VirtualFileSystem | null>(null);
  const filesRef = useRef<VirtualFileSystem | null>(null);
  const [messages, setMessages] = useState<
    Array<{ role: 'user' | 'assistant'; content: string; timestamp?: string }>
  >([]);
  const [isSending, setIsSending] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [sdkUpdate, setSdkUpdate] = useState<{
    available: boolean;
    from: string | null;
    to: string;
    hasBreaking: boolean;
  } | null>(null);
  const [isUpgradingSdk, setIsUpgradingSdk] = useState(false);
  const [requestId, setRequestId] = useState<string | null>(null);
  const [guidedFlow, setGuidedFlow] = useState<GuidedFlow | null>(null);
  const [buildPlan, setBuildPlan] = useState<BuildPlan | null>(null);
  const [builderMode, setBuilderMode] = useState<BuilderMode>('guided');
  const [isGuidedLoading, setIsGuidedLoading] = useState(false);
  const [guidedError, setGuidedError] = useState<string | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [isBuildingPreview, setIsBuildingPreview] = useState(false);
  const [buildError, setBuildError] = useState<string | null>(null);

  // Track active guided request for cancellation
  const guidedAbortRef = useRef<AbortController | null>(null);

  // Track guided stream request ID — when set, onComplete knows to handle as guided flow
  const guidedStreamRequestIdRef = useRef<string | null>(null);

  // Deferred guided result — stores the API result so the page can show
  // the AI message first, then apply the structured result when Continue is clicked
  const pendingGuidedResultRef = useRef<{
    guidedFlow?: GuidedFlow;
    buildPlan?: BuildPlan | null;
  } | null>(null);
  const [hasPendingGuidedResult, setHasPendingGuidedResult] = useState(false);

  // Track accumulated streaming text per node
  const streamingTextRef = useRef<string>('');

  // Refs to avoid stale closures in callbacks
  const verboseRef = useRef(verbose);
  verboseRef.current = verbose;
  const useOpusRef = useRef(useOpus);
  useOpusRef.current = useOpus;
  const onVerboseEventRef = useRef(onVerboseEvent);
  onVerboseEventRef.current = onVerboseEvent;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const sessionIdRef = useRef<string | null>(null);
  // Forward ref for buildPreview — assigned after declaration, used in early callbacks
  const buildPreviewRef = useRef<(files?: VirtualFileSystem) => Promise<string | null>>(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    null as any,
  );
  const guidedFlowRef = useRef(guidedFlow);
  guidedFlowRef.current = guidedFlow;

  // Accumulates the latest files during streaming; snapshot is flushed once in onComplete
  const pendingSnapshotFilesRef = useRef<VirtualFileSystem | null>(null);

  // ─── Undo/Redo ──────────────────────────────────────────────────────────────

  const {
    canUndo,
    canRedo,
    undoCount,
    redoCount,
    snapshot: takeSnapshot,
    undo: undoHistory,
    redo: redoHistory,
    getHistory,
    restore: restoreHistory,
  } = useUndoRedo<VirtualFileSystem>({ maxHistory: 50 });

  const takeSnapshotRef = useRef(takeSnapshot);
  takeSnapshotRef.current = takeSnapshot;
  const getHistoryRef = useRef(getHistory);
  getHistoryRef.current = getHistory;

  // Debounced save timer for IndexedDB persistence
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced backend persist timer for undo/redo file syncing
  const backendSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleBackendSave = useCallback((filesToSave: VirtualFileSystem) => {
    const sid = sessionIdRef.current;
    if (!sid) return;
    if (backendSaveTimerRef.current) clearTimeout(backendSaveTimerRef.current);
    backendSaveTimerRef.current = setTimeout(() => {
      api
        .updatePluginBuilderSessionFiles(sid, filesToSave as Record<string, unknown>)
        .catch((err) => {
          console.warn('[usePluginBuilderSession] Failed to persist undo/redo files:', err);
        });
    }, 500);
  }, []);

  const scheduleSave = useCallback(() => {
    const sid = sessionIdRef.current;
    if (!sid) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      const { states, index } = getHistoryRef.current();
      if (states.length > 0) {
        saveHistory(sid, states, index);
      }
    }, 1_000);
  }, []);

  const scheduleSaveRef = useRef(scheduleSave);
  scheduleSaveRef.current = scheduleSave;

  // Load undo history from IndexedDB on session load
  const loadUndoHistory = useCallback(
    async (sessionId: string) => {
      const saved = await loadHistory(sessionId);
      if (saved && saved.states.length > 0) {
        restoreHistory(saved.states as VirtualFileSystem[], saved.index);
      }
      // Cleanup old entries
      cleanupOldEntries();
    },
    [restoreHistory],
  );

  // Flush pending saves on unmount
  useEffect(() => {
    return () => {
      // Flush any pending snapshot from an in-flight stream
      if (pendingSnapshotFilesRef.current) {
        takeSnapshotRef.current(pendingSnapshotFilesRef.current);
        pendingSnapshotFilesRef.current = null;
      }
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        const sid = sessionIdRef.current;
        if (sid) {
          const { states, index } = getHistoryRef.current();
          if (states.length > 0) {
            saveHistory(sid, states, index);
          }
        }
      }
      if (backendSaveTimerRef.current) {
        clearTimeout(backendSaveTimerRef.current);
      }
    };
  }, []);

  // ─── Streaming ──────────────────────────────────────────────────────────────

  const streaming = useStreaming({
    requestId,
    authToken,
    endpoint: WEBSOCKET_ENDPOINT,
    onTextUpdate: useCallback((_nodeId: string, text: string) => {
      streamingTextRef.current += text;
    }, []),
    onSettingsUpdate: useCallback((settings: Record<string, unknown>) => {
      // During guided streaming, don't apply guidedFlow/buildPlan from settings_update events
      // — the structured result comes via the complete event payload instead
      if (guidedStreamRequestIdRef.current) return;
      if (settings.guidedFlow) {
        setGuidedFlow(settings.guidedFlow as GuidedFlow);
      }
      if (settings.buildPlan) {
        setBuildPlan(settings.buildPlan as BuildPlan);
      }
    }, []),
    onFilesUpdate: useCallback((updatedFiles: Record<string, unknown>) => {
      const vfs = updatedFiles as VirtualFileSystem;
      setFiles(vfs);
      filesRef.current = vfs;
      // Accumulate latest files — snapshot is taken once when the stream completes
      // (not per tool call) so undo reverts the entire AI response, not individual edits
      pendingSnapshotFilesRef.current = vfs;
    }, []),
    onComplete: useCallback(
      (completedRequestId: string, payload?: Record<string, unknown>) => {
        const isGuidedStream = guidedStreamRequestIdRef.current === completedRequestId;

        if (isGuidedStream) {
          // Guided stream completed — apply the result from the payload
          guidedStreamRequestIdRef.current = null;
          setIsGuidedLoading(false);

          // Add assistant message from accumulated streaming text
          const assistantText = streamingTextRef.current.trim();
          if (assistantText) {
            setMessages((prev) => [
              ...prev,
              { role: 'assistant', content: assistantText, timestamp: new Date().toISOString() },
            ]);
          }
          streamingTextRef.current = '';
          setRequestId(null);

          // Stash the structured result from the complete event payload
          const guidedResult = payload?.guidedResult as Record<string, unknown> | undefined;
          if (guidedResult) {
            pendingGuidedResultRef.current = {
              guidedFlow: guidedResult.guidedFlow as GuidedFlow,
              buildPlan: (guidedResult.buildPlan as BuildPlan) ?? null,
            };
            setHasPendingGuidedResult(true);
          }

          // Flush pending snapshot — one snapshot per AI response (not per tool call)
          if (pendingSnapshotFilesRef.current) {
            takeSnapshotRef.current(pendingSnapshotFilesRef.current);
            scheduleSaveRef.current();
            pendingSnapshotFilesRef.current = null;
          }

          // Build preview with current files after guided stream completes
          const guidedFilesToBuild = filesRef.current;
          if (guidedFilesToBuild && Object.keys(guidedFilesToBuild).length > 0) {
            buildPreviewRef.current(guidedFilesToBuild);
          }

          // Re-fetch session to update credit usage (totalCostCents).
          // Only update the session object — guidedFlow/buildPlan come from the payload above.
          const guidedSid = sessionIdRef.current;
          if (guidedSid) {
            api.getPluginBuilderSession(guidedSid).then((result) => {
              setSession(result.session as PluginBuilderSession);
            });
          }
          return;
        }

        // Regular chat stream completed
        setIsSending(false);
        setRequestId(null);

        // Add assistant message from accumulated text
        const assistantText = streamingTextRef.current.trim();
        if (assistantText) {
          setMessages((prev) => [
            ...prev,
            { role: 'assistant', content: assistantText, timestamp: new Date().toISOString() },
          ]);
        }
        streamingTextRef.current = '';

        // Flush pending snapshot — one snapshot per AI response (not per tool call)
        if (pendingSnapshotFilesRef.current) {
          takeSnapshotRef.current(pendingSnapshotFilesRef.current);
          scheduleSaveRef.current();
          pendingSnapshotFilesRef.current = null;
        }

        // Build preview with current files after stream completes
        const filesToBuild = filesRef.current;
        if (filesToBuild && Object.keys(filesToBuild).length > 0) {
          buildPreviewRef.current(filesToBuild);
        }

        // Re-fetch session to get latest state (credit usage, guided flow, build plan).
        // DO NOT setFiles here — files are already current from onFilesUpdate events.
        // Re-fetching would overwrite any undo the user performed while the stream was completing.
        const sid = sessionIdRef.current;
        if (sid) {
          api.getPluginBuilderSession(sid).then((result) => {
            const sess = result.session as PluginBuilderSession;
            setSession(sess);
            const settings = sess.currentSettings as Record<string, unknown>;
            if (settings?.guidedFlow) {
              setGuidedFlow(settings.guidedFlow as GuidedFlow);
            }
            if (settings?.buildPlan) {
              setBuildPlan(settings.buildPlan as BuildPlan);
            }
          });
        }
      },
      [], // Using ref instead of session?.sessionId dependency
    ),
    onError: useCallback((_code: string, message: string) => {
      // If this error came from a guided stream, surface it as a guided error
      // and clear guided loading state so the user isn't left waiting
      if (guidedStreamRequestIdRef.current) {
        guidedStreamRequestIdRef.current = null;
        setIsGuidedLoading(false);
        setGuidedError(message);
      }
      setError(message);
      setIsSending(false);
      onErrorRef.current?.(new Error(message));
      // Flush pending snapshot so partial changes from a failed stream are still undoable
      if (pendingSnapshotFilesRef.current) {
        takeSnapshotRef.current(pendingSnapshotFilesRef.current);
        scheduleSaveRef.current();
        pendingSnapshotFilesRef.current = null;
      }
      // Build preview with whatever partial files exist
      const errorFiles = filesRef.current;
      if (errorFiles && Object.keys(errorFiles).length > 0) {
        buildPreviewRef.current(errorFiles);
      }
    }, []),
    // Verbose event callbacks
    onSystemPrompt: useCallback((prompt: string) => {
      if (verboseRef.current && onVerboseEventRef.current) {
        onVerboseEventRef.current({
          type: 'system_prompt',
          timestamp: new Date().toISOString(),
          data: { prompt },
        });
      }
    }, []),
    onToolCall: useCallback((toolName: string, toolInput: unknown) => {
      if (verboseRef.current && onVerboseEventRef.current) {
        onVerboseEventRef.current({
          type: 'tool_call',
          timestamp: new Date().toISOString(),
          data: { toolName, toolInput },
        });
      }
    }, []),
    onToolResult: useCallback((toolName: string, toolResult: unknown, isError: boolean) => {
      if (verboseRef.current && onVerboseEventRef.current) {
        onVerboseEventRef.current({
          type: 'tool_result',
          timestamp: new Date().toISOString(),
          data: { toolName, toolResult, isError },
        });
      }
    }, []),
    onModelInfo: useCallback((model: string, intent: string) => {
      if (verboseRef.current && onVerboseEventRef.current) {
        onVerboseEventRef.current({
          type: 'model_info',
          timestamp: new Date().toISOString(),
          data: { model, intent },
        });
      }
    }, []),
    onToolsAvailable: useCallback((tools: Array<{ name: string; description: string }>) => {
      if (verboseRef.current && onVerboseEventRef.current) {
        onVerboseEventRef.current({
          type: 'tools_available',
          timestamp: new Date().toISOString(),
          data: { tools },
        });
      }
    }, []),
  });

  // Current streaming text (combined from all nodes)
  const currentStreamingText = useMemo(
    () => Object.values(streaming.streamingText).join(''),
    [streaming.streamingText],
  );

  // ─── Session Actions ────────────────────────────────────────────────────────

  const loadSession = useCallback(
    async (sessionId: string) => {
      setIsLoading(true);
      setError(null);
      sessionIdRef.current = sessionId;
      try {
        const result = await api.getPluginBuilderSession(sessionId);
        const sess = result.session as PluginBuilderSession;
        setSession(sess);
        setMessages(
          sess.conversationHistory.map((m) => ({
            role: m.role,
            content: m.content,
            timestamp: m.timestamp,
          })),
        );

        const settings = sess.currentSettings as Record<string, unknown>;
        if (settings?.currentFiles) {
          const loadedFiles = settings.currentFiles as VirtualFileSystem;
          setFiles(loadedFiles);
          filesRef.current = loadedFiles;
        }
        if (settings?.guidedFlow) {
          setGuidedFlow(settings.guidedFlow as GuidedFlow);
        }
        if (settings?.buildPlan) {
          setBuildPlan(settings.buildPlan as BuildPlan);
        }

        // Extract SDK update info from session response
        const sessionWithSdkUpdate = result.session as PluginBuilderSession & {
          sdkUpdate?: { available: boolean; from: string | null; to: string; hasBreaking: boolean };
        };
        setSdkUpdate(sessionWithSdkUpdate.sdkUpdate ?? null);

        // Load undo/redo history from IndexedDB.
        // If there is a restored undo position, apply those files instead of the server's
        // so that undo state survives page refresh.
        await loadUndoHistory(sessionId);
        const { states, index } = getHistoryRef.current();
        if (states.length > 0 && index >= 0 && index < states.length) {
          setFiles(states[index]);
          filesRef.current = states[index];
        }

        // Build preview with the finalized files
        const finalFiles = filesRef.current;
        if (finalFiles && Object.keys(finalFiles).length > 0) {
          buildPreviewRef.current(finalFiles);
        }
      } catch (err) {
        const message = (err as Error).message;
        setError(message);
        onErrorRef.current?.(err as Error);
      } finally {
        setIsLoading(false);
      }
    },
    [loadUndoHistory],
  );

  const sendMessage = useCallback(
    async (message: string, guidedPhase?: GuidedPhase, autoApprove?: boolean) => {
      const sid = sessionIdRef.current;
      if (!sid || isSending) return;

      setIsSending(true);
      setError(null);
      streamingTextRef.current = '';

      // Add user message to local state immediately
      setMessages((prev) => [
        ...prev,
        { role: 'user', content: message, timestamp: new Date().toISOString() },
      ]);

      try {
        const result = await api.startPluginBuilderMessageStream(
          sid,
          message,
          verboseRef.current,
          useOpusRef.current,
          guidedPhase,
          autoApprove,
        );
        setRequestId(result.requestId);
      } catch (err) {
        const errMessage = (err as Error).message;
        setError(errMessage);
        setIsSending(false);
        onErrorRef.current?.(err as Error);
      }
    },
    [isSending],
  );

  const advanceStep = useCallback(async () => {
    const sid = sessionIdRef.current;
    if (!sid) return;
    try {
      const result = await api.advancePluginBuilderStep(sid);
      // Update local build plan state from the server response
      if (result.buildPlan) {
        setBuildPlan(result.buildPlan as BuildPlan);
      }
    } catch (err) {
      setError((err as Error).message);
    }
  }, []);

  const startGuidedFlow = useCallback(async (idea: string) => {
    const sid = sessionIdRef.current;
    if (!sid) return;

    setIsGuidedLoading(true);
    setGuidedError(null);
    streamingTextRef.current = '';

    // Add user message immediately so conversation view appears during loading
    setMessages((prev) => [
      ...prev,
      {
        role: 'user',
        content: `I want to build an Arche: ${idea}`,
        timestamp: new Date().toISOString(),
      },
    ]);

    try {
      const result = await api.startPluginGuidedFlow(sid, idea, verboseRef.current);
      guidedStreamRequestIdRef.current = result.requestId;
      setRequestId(result.requestId); // Triggers WebSocket subscription
      setBuilderMode('guided');
    } catch (err) {
      const message = (err as Error).message;
      setGuidedError(message);
      setError(message);
      setIsGuidedLoading(false);
      onErrorRef.current?.(err as Error);
    }
  }, []);

  const submitAnswers = useCallback(async (answers: Record<number, string>, comment?: string) => {
    const sid = sessionIdRef.current;
    if (!sid) return;

    setIsGuidedLoading(true);
    setGuidedError(null);
    streamingTextRef.current = '';

    // Add user message immediately so conversation view shows during loading
    const qaList = guidedFlowRef.current?.clarifyingQA;
    const answersSummary = Object.entries(answers)
      .map(([i, a]) => {
        const question = qaList?.[Number(i)]?.question ?? `Q${Number(i) + 1}`;
        return `Q: ${question}\nA: ${a}`;
      })
      .join('\n\n');
    setMessages((prev) => [
      ...prev,
      {
        role: 'user',
        content: `Here are my answers:\n${answersSummary}${comment ? `\n\nAdditional context: ${comment}` : ''}`,
        timestamp: new Date().toISOString(),
      },
    ]);

    try {
      const result = await api.submitGuidedAnswers(
        sid,
        answers,
        verboseRef.current,
        undefined,
        comment,
      );
      guidedStreamRequestIdRef.current = result.requestId;
      setRequestId(result.requestId); // Triggers WebSocket subscription
    } catch (err) {
      const message = (err as Error).message;
      setGuidedError(message);
      setIsGuidedLoading(false);
      onErrorRef.current?.(err as Error);
    }
  }, []);

  const approvePlan = useCallback(async () => {
    const sid = sessionIdRef.current;
    if (!sid) return;

    setIsGuidedLoading(true);
    setGuidedError(null);
    streamingTextRef.current = '';

    // Add user message immediately
    setMessages((prev) => [
      ...prev,
      {
        role: 'user',
        content: 'I approve this build plan.',
        timestamp: new Date().toISOString(),
      },
    ]);

    try {
      const result = await api.submitGuidedPlanDecision(sid, 'approve', verboseRef.current);
      guidedStreamRequestIdRef.current = result.requestId;
      setRequestId(result.requestId); // Triggers WebSocket subscription
    } catch (err) {
      const message = (err as Error).message;
      setGuidedError(message);
      setIsGuidedLoading(false);
      onErrorRef.current?.(err as Error);
    }
  }, []);

  const requestPlanChanges = useCallback(async (feedback: string) => {
    const sid = sessionIdRef.current;
    if (!sid) return;

    setIsGuidedLoading(true);
    setGuidedError(null);
    streamingTextRef.current = '';

    // Add user message immediately
    setMessages((prev) => [
      ...prev,
      {
        role: 'user',
        content: `I'd like changes to the plan: ${feedback}`,
        timestamp: new Date().toISOString(),
      },
    ]);

    try {
      const result = await api.submitGuidedPlanDecision(
        sid,
        'request-changes',
        verboseRef.current,
        feedback,
      );
      guidedStreamRequestIdRef.current = result.requestId;
      setRequestId(result.requestId); // Triggers WebSocket subscription
    } catch (err) {
      const message = (err as Error).message;
      setGuidedError(message);
      setIsGuidedLoading(false);
      onErrorRef.current?.(err as Error);
    }
  }, []);

  const approveAssumptions = useCallback(async () => {
    const sid = sessionIdRef.current;
    if (!sid) return;

    // Add user message immediately so conversation has a visible record of the approval
    setMessages((prev) => [
      ...prev,
      {
        role: 'user',
        content: "I approve these assumptions. Let's start building.",
        timestamp: new Date().toISOString(),
      },
    ]);

    const controller = new AbortController();
    guidedAbortRef.current = controller;
    setIsGuidedLoading(true);
    setGuidedError(null);
    try {
      const result = await api.submitGuidedAssumptionDecision(
        sid,
        'approve',
        undefined,
        undefined,
        controller.signal,
      );
      // Approve is local-only (no Claude call), apply immediately
      setGuidedFlow(result.guidedFlow as GuidedFlow);
      if (result.buildPlan) {
        setBuildPlan(result.buildPlan as BuildPlan);
      }
    } catch (err) {
      if (controller.signal.aborted) return;
      const message = (err as Error).message;
      setGuidedError(message);
      onErrorRef.current?.(err as Error);
    } finally {
      guidedAbortRef.current = null;
      setIsGuidedLoading(false);
    }
  }, []);

  const suggestAssumptionChanges = useCallback(async (comments: Record<string, string>) => {
    const sid = sessionIdRef.current;
    if (!sid) return;

    setIsGuidedLoading(true);
    setGuidedError(null);
    streamingTextRef.current = '';

    // Add user message immediately — include the assumption text for context
    const assumptions = guidedFlowRef.current?.assumptions ?? [];
    const feedbackSummary = Object.entries(comments)
      .map(([id, fb]) => {
        const assumption = assumptions.find((a) => a.id === id);
        const label = assumption?.assumption ?? id;
        return `Assumption: ${label}\nFeedback: ${fb}`;
      })
      .join('\n\n');
    setMessages((prev) => [
      ...prev,
      {
        role: 'user',
        content: `I have feedback on some assumptions:\n\n${feedbackSummary}`,
        timestamp: new Date().toISOString(),
      },
    ]);

    try {
      const result = await api.submitGuidedAssumptionDecision(
        sid,
        'suggest-changes',
        verboseRef.current,
        comments,
      );
      if (result.requestId) {
        guidedStreamRequestIdRef.current = result.requestId;
        setRequestId(result.requestId); // Triggers WebSocket subscription
      }
    } catch (err) {
      const message = (err as Error).message;
      setGuidedError(message);
      setIsGuidedLoading(false);
      onErrorRef.current?.(err as Error);
    }
  }, []);

  const applyPendingGuidedResult = useCallback(() => {
    const pending = pendingGuidedResultRef.current;
    if (!pending) return;

    if (pending.guidedFlow) {
      setGuidedFlow(pending.guidedFlow);
    }
    if (pending.buildPlan !== undefined) {
      setBuildPlan(pending.buildPlan);
    }

    pendingGuidedResultRef.current = null;
    setHasPendingGuidedResult(false);
  }, []);

  const cancelGuidedRequest = useCallback(() => {
    if (guidedAbortRef.current) {
      guidedAbortRef.current.abort();
      guidedAbortRef.current = null;
    }

    // Clear guided stream state
    guidedStreamRequestIdRef.current = null;
    streamingTextRef.current = '';
    setRequestId(null);
    setIsGuidedLoading(false);
    setGuidedError(null);

    // Re-fetch session to pick up server-side state changes (e.g., saved answers)
    const sid = sessionIdRef.current;
    if (sid) {
      api
        .getPluginBuilderSession(sid)
        .then((result) => {
          const sess = result.session as PluginBuilderSession;
          const settings = sess.currentSettings as Record<string, unknown>;
          if (settings?.guidedFlow) {
            setGuidedFlow(settings.guidedFlow as GuidedFlow);
          }
          if (settings?.buildPlan) {
            setBuildPlan(settings.buildPlan as BuildPlan);
          }
        })
        .catch(() => {
          // Best effort — don't fail on re-fetch
        });
    }
  }, []);

  const rewindToPhase = useCallback(async (targetPhase: GuidedPhase) => {
    const sid = sessionIdRef.current;
    if (!sid) return;

    // Cancel any in-flight guided request
    if (guidedAbortRef.current) {
      guidedAbortRef.current.abort();
      guidedAbortRef.current = null;
    }
    setIsGuidedLoading(false);
    setIsSending(false);

    // Clear any pending guided result so it doesn't interfere
    pendingGuidedResultRef.current = null;
    setHasPendingGuidedResult(false);

    try {
      const result = await api.rewindGuidedPhase(sid, targetPhase);
      setGuidedFlow(result.guidedFlow as GuidedFlow);
      if (result.buildPlan) {
        setBuildPlan(result.buildPlan as BuildPlan);
      } else {
        setBuildPlan(null);
      }
      setBuilderMode('guided');
    } catch (err) {
      onErrorRef.current?.(err as Error);
    }
  }, []);

  const saveSession = useCallback(async (name: string, description?: string) => {
    const sid = sessionIdRef.current;
    if (!sid) return;

    try {
      await api.savePluginBuilderSession(sid, { name, description });
      setSession((prev) => (prev ? { ...prev, currentName: name } : null));
    } catch (err) {
      setError((err as Error).message);
      onErrorRef.current?.(err as Error);
    }
  }, []);

  const resetContext = useCallback(async () => {
    const sid = sessionIdRef.current;
    if (!sid) return;

    setIsResetting(true);
    try {
      await api.resetPluginBuilderContext(sid);
      setMessages([]);
    } catch (err) {
      setError((err as Error).message);
      onErrorRef.current?.(err as Error);
    } finally {
      setIsResetting(false);
    }
  }, []);

  const stopGeneration = useCallback(() => {
    if (!isSending) return;

    // Capture partial text
    const partialText = streamingTextRef.current.trim();
    if (partialText) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: partialText + '\n\n_(Generation stopped)_',
          timestamp: new Date().toISOString(),
        },
      ]);
    }

    // Reset streaming state
    streaming.reset();
    setRequestId(null);
    setIsSending(false);
    streamingTextRef.current = '';
  }, [isSending, streaming]);

  const uploadPreviewScreenshot = useCallback((dataUrl: string) => {
    const sid = sessionIdRef.current;
    if (!sid) return;
    api.uploadPluginPreviewScreenshot(sid, dataUrl).catch((err) => {
      console.warn('[usePluginBuilderSession] Failed to upload preview screenshot:', err);
    });
  }, []);

  // ─── Preview ────────────────────────────────────────────────────────────────

  // Monotonic counter to discard stale build results.
  // When undo triggers a new build while the auto-build is still in-flight,
  // the auto-build's response must not overwrite the undo's newer result.
  const buildSeqRef = useRef(0);

  const buildPreview = useCallback(
    async (filesToBuild?: VirtualFileSystem): Promise<string | null> => {
      const sid = sessionIdRef.current;
      if (!sid) return null;

      const seq = ++buildSeqRef.current;
      setIsBuildingPreview(true);
      setBuildError(null);

      try {
        const result = await api.buildPluginPreview(
          sid,
          filesToBuild as Record<string, unknown> | undefined,
        );
        // Discard result if a newer build was started while this one was in-flight
        if (buildSeqRef.current !== seq) return null;
        if (result.success) {
          setPreviewHtml(result.html);
          return result.html;
        }
        return null;
      } catch (err) {
        if (buildSeqRef.current !== seq) return null;
        setBuildError((err as Error).message);
        return null;
      } finally {
        // Only clear the building flag if this is still the latest build
        if (buildSeqRef.current === seq) {
          setIsBuildingPreview(false);
        }
      }
    },
    [],
  );

  // Keep the ref up to date so []‑dep callbacks (onComplete, onError) can call buildPreview
  buildPreviewRef.current = buildPreview;

  // ─── Undo/Redo Actions ──────────────────────────────────────────────────────

  const undo = useCallback(() => {
    const previousState = undoHistory();
    if (previousState !== null) {
      setFiles(previousState);
      filesRef.current = previousState;
      scheduleSaveRef.current();
      scheduleBackendSave(previousState);
      // Direct build for immediate feedback; buildSeqRef discards stale results
      buildPreview(previousState);
      addBreadcrumb('undo-redo', 'Undo applied', {
        fileCount: Object.keys(previousState).length,
      });
    }
  }, [undoHistory, scheduleBackendSave, buildPreview]);

  const redo = useCallback(() => {
    const nextState = redoHistory();
    if (nextState !== null) {
      setFiles(nextState);
      filesRef.current = nextState;
      scheduleSaveRef.current();
      scheduleBackendSave(nextState);
      // Direct build for immediate feedback; buildSeqRef discards stale results
      buildPreview(nextState);
      addBreadcrumb('undo-redo', 'Redo applied', {
        fileCount: Object.keys(nextState).length,
      });
    }
  }, [redoHistory, scheduleBackendSave, buildPreview]);

  const upgradeSdk = useCallback(async () => {
    const sid = sessionIdRef.current;
    if (!sid) return;

    setIsUpgradingSdk(true);
    try {
      const result = await api.upgradePluginBuilderSdk(sid);
      const sess = result.session as PluginBuilderSession;
      setSession(sess);

      const settings = sess.currentSettings as Record<string, unknown>;
      if (settings?.currentFiles) {
        const loadedFiles = settings.currentFiles as VirtualFileSystem;
        setFiles(loadedFiles);
        filesRef.current = loadedFiles;
      }

      setSdkUpdate(null);

      // If breaking changes, trigger AI migration
      if (result.triggerAiMigration) {
        await sendMessage(
          'Please review my plugin code against the updated SDK and fix any incompatibilities. Show me what you changed.',
        );
      }

      // Rebuild preview
      if (filesRef.current && Object.keys(filesRef.current).length > 0) {
        buildPreviewRef.current(filesRef.current);
      }
    } catch (err) {
      setError((err as Error).message);
      onErrorRef.current?.(err as Error);
    } finally {
      setIsUpgradingSdk(false);
    }
  }, [sendMessage]);

  return {
    session,
    isLoading,
    error,
    files,
    messages,
    streamingText: currentStreamingText,
    isSending,
    guidedFlow,
    buildPlan,
    builderMode,
    isGuidedLoading,
    guidedError,
    canUndo,
    canRedo,
    undoCount,
    redoCount,
    undo,
    redo,
    loadSession,
    sendMessage,
    startGuidedFlow,
    setBuilderMode,
    saveSession,
    resetContext,
    stopGeneration,
    uploadPreviewScreenshot,
    advanceStep,
    submitAnswers,
    approvePlan,
    requestPlanChanges,
    approveAssumptions,
    suggestAssumptionChanges,
    applyPendingGuidedResult,
    hasPendingGuidedResult,
    cancelGuidedRequest,
    rewindToPhase,
    buildPreview,
    previewHtml,
    isBuildingPreview,
    buildError,
    isResetting,
    sdkUpdate,
    isUpgradingSdk,
    upgradeSdk,
    setSessionName: useCallback(
      (name: string) => setSession((prev) => (prev ? { ...prev, currentName: name } : null)),
      [],
    ),
    updateSessionSettings: useCallback(
      (settings: Record<string, unknown>) =>
        setSession((prev) =>
          prev
            ? {
                ...prev,
                currentSettings: {
                  ...((prev.currentSettings as Record<string, unknown>) || {}),
                  ...settings,
                },
              }
            : null,
        ),
      [],
    ),
    setBuildPlan,
  };
}
